'use client';
import { useId } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar, A11y } from 'swiper/modules';
import NextImage from 'next/image';
import { cn } from 'lib/utils';
import { type ImageField, type LinkField } from 'lib/types';
import { type IconNames } from 'atoms/Icon/Icons';
import {
  type BloomProps,
  Bloom,
  CarouselNavigation,
  Icon,
  Button,
  Wrapper,
} from 'ui';

export type MetricsCarouselProps = {
  className?: string;
  title?: string;
  items: {
    title: string;
    intro: string;
    link?: LinkField;
    metrics: {
      label: string;
      icon: IconNames;
    }[];
    image: ImageField;
  }[];
  bloom?: BloomProps;
};

const MetricsCarousel = ({
  title,
  items,
  className,
  bloom,
}: MetricsCarouselProps) => {
  const id = useId().replaceAll(':', ''); // Remove colons as an SSR-friendly alternative to CSS.escape()

  return (
    <Wrapper
      variant="page-full"
      className={cn('relative pr-0', className)}
      as="section"
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      <Bloom {...bloom} />

      {title && (
        <h2 className="relative z-2 mb-10 md:md-12" id={`${id}-title`}>
          {title}
        </h2>
      )}

      <Swiper
        modules={[Navigation, Scrollbar, A11y]}
        navigation={{
          nextEl: `#${id}-next`,
          prevEl: `#${id}-prev`,
        }}
        scrollbar={{
          el: `#${id}-scrollbar`,
          draggable: false,
        }}
        spaceBetween={24}
        slidesPerView="auto"
        className="z-2"
      >
        {items.map((item, index) => {
          const isExternalLink = item.link?.target === '_blank';

          return (
            <SwiperSlide key={index} className="w-fit max-w-73 md:max-w-94">
              <article className="relative w-73 md:w-94 h-169 md:h-196 px-4 md:px-8 flex flex-col justify-end">
                {/* Content */}
                <div className="min-h-106 md:min-h-112 z-2 p-4 md:p-6 flex flex-col justify-end gap-5 md:gap-6 bg-gradient-to-t from-stone-700/75 to-stone-700/50 backdrop-blur-xl rounded-lg">
                  <header className="flex flex-col gap-2 pb-5 md:pb-6 border-b border-clay-50/20">
                    <h3 className="text-h5" id={`${id}-label-${index}`}>
                      {item.title}
                    </h3>
                    <p className="text-p3">{item.intro}</p>
                  </header>

                  <ul className="grow flex flex-col gap-4">
                    {item.metrics.map((metric, index) => (
                      <li key={index} className="flex items-center gap-6">
                        <span className="size-6 shrink-0 flex justify-center">
                          <Icon
                            name={metric.icon}
                            className="size-auto fill-clay-50"
                          />
                        </span>
                        <span className="text-p3">{metric.label}</span>
                      </li>
                    ))}
                  </ul>

                  {!!item.link && (
                    <div>
                      <Button
                        href={item.link.href}
                        target={item.link.target}
                        variant="tertiary"
                        aria-labelledby={`${id}-label-${index}`}
                        className="w-full flex items-center justify-center gap-2"
                        textSize="sm"
                        trackingProps={item.link.trackingProps}
                      >
                        <span>{item.link.text || item.title}</span>
                        <Icon
                          name={
                            isExternalLink
                              ? 'external-link-arrow'
                              : 'internal-link-arrow-right'
                          }
                          className={cn('h-auto shrink-0', {
                            'w-3.25': !isExternalLink,
                            'w-3': isExternalLink,
                          })}
                        />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Image */}
                <div className="absolute left-0 top-0 w-full h-130 md:h-165">
                  <NextImage
                    src={item.image.src}
                    alt={item.title}
                    fill={true}
                    className="absolute w-full h-auto object-cover rounded-lg"
                    role="presentation"
                  />
                </div>
              </article>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <CarouselNavigation
        scrollbarId={`${id}-scrollbar`}
        prevId={`${id}-prev`}
        nextId={`${id}-next`}
      />
    </Wrapper>
  );
};

export default MetricsCarousel;
